/**
 * t466u: the workspace "Track overtime" switch. On (the default) keeps the weekly threshold behaviour; off means
 * there is no overtime anywhere: every paid hour is a normal hour, and reports, exports and help drop the overtime split.
 * Pay is unchanged either way (overtime was always costed at the normal rate, inside Base pay).
 */
export interface OvertimeSettings { overtime_enabled?: boolean | null; overtime_weekly_hours?: number | string | null }

export const OVERTIME_SWITCH_LABEL = 'Track overtime';
/** What the assistant and anything else explaining hours says when the switch is off. */
export const OVERTIME_OFF_NOTE = 'This workspace doesn\u2019t track overtime: all hours are normal hours.';
export const OVERTIME_THRESHOLD_LABEL = 'Overtime threshold (hours/week)';

/** Missing (older API / cached settings) counts as on, which is the existing behaviour. */
export const overtimeEnabled = (s: OvertimeSettings | null | undefined): boolean => s?.overtime_enabled !== false;

/** Weekly threshold in minutes, or null when overtime isn't tracked. */
export function overtimeThresholdMinutes(s: OvertimeSettings | null | undefined): number | null {
  if (!overtimeEnabled(s)) return null;
  const h = Number(s?.overtime_weekly_hours ?? 40);
  return (Number.isFinite(h) ? h : 40) * 60;
}

/** Overtime minutes for one person's week of paid minutes (0 when off or under the threshold). */
export function weeklyOvertimeMinutes(weekMinutes: number, s: OvertimeSettings | null | undefined): number {
  const thr = overtimeThresholdMinutes(s);
  return thr == null ? 0 : Math.max(0, weekMinutes - thr);
}

/** Split a week's paid minutes into normal and overtime. Off: everything is normal. */
export function splitWeeklyHours(weekMinutes: number, s: OvertimeSettings | null | undefined): { normal_minutes: number; overtime_minutes: number } {
  const ot = weeklyOvertimeMinutes(weekMinutes, s);
  return { normal_minutes: weekMinutes - ot, overtime_minutes: ot };
}

/** Explanatory line used under reports, or null when overtime isn't tracked. */
export function overtimeNote(s: OvertimeSettings | null | undefined): string | null {
  return overtimeEnabled(s) ? `Overtime = approved hours above ${Number(s?.overtime_weekly_hours ?? 40)}h a week.` : null;
}

/** Report catalogue text without overtime when it's off ("base, premium, overtime and breaks" -> "base, premium and breaks"). */
export function withoutOvertimeText(text: string, s: OvertimeSettings | null | undefined): string {
  if (overtimeEnabled(s)) return text;
  return text.replace(/, overtime and /gi, ' and ').replace(/, overtime,/gi, ',').replace(/ and overtime\b/gi, '').replace(/\s*Overtime = [^.]*\.\s*/g, ' ').trim();
}
